//SCR-052 (S2.4.7): modal cấp vai trò. Lựa chọn chỉ dùng nhãn nghiệp vụ từ catalog đã
//published; mỗi grant mang ĐÚNG một chiều phạm vi (đơn vị | địa bàn | toàn hệ thống)
//theo hình thái catalog v1 — quyết định implementation, backend vẫn là thực quyền và
//từ chối tổ hợp ngoài catalog bằng 422 nghiệp vụ.
import { Alert, DatePicker, Form, Modal, Radio, Select } from "antd";
import type { Dayjs } from "dayjs";
import { useState } from "react";
import { describeAdminError } from "../administration/commandErrors";
import {
  SYSTEM_SCOPE_ID,
  SYSTEM_SCOPE_TYPE,
} from "./roleAssignmentAdminApi";
import type {
  AuthorizationCatalogPublished,
  GrantRoleAssignmentInput,
} from "./roleAssignmentAdminApi";
import "./role-assignments.css";

type ScopeChoice = "ORGANIZATION" | "AREA" | "SYSTEM";

export type GrantFormValues = GrantRoleAssignmentInput;

export interface GrantModalError {
  readonly code: string;
  readonly requestId?: string | undefined;
}

export function GrantRoleAssignmentModal({
  open,
  accountLabel,
  catalog,
  orgOptions,
  areaOptions,
  submitting,
  error,
  onCancel,
  onSubmit,
}: {
  readonly open: boolean;
  readonly accountLabel: string;
  readonly catalog: AuthorizationCatalogPublished;
  readonly orgOptions: readonly { id: string; name: string }[];
  readonly areaOptions: readonly { id: string; name: string }[];
  readonly submitting: boolean;
  readonly error: GrantModalError | null;
  readonly onCancel: () => void;
  readonly onSubmit: (values: GrantFormValues) => void;
}) {
  const grantableRoles = catalog.roles;
  const [roleCode, setRoleCode] = useState<string | null>(null);
  const [scope, setScope] = useState<ScopeChoice | null>(null);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [areaId, setAreaId] = useState<string | null>(null);
  const [immediate, setImmediate] = useState(true);
  const [validFromDate, setValidFromDate] = useState<Dayjs | null>(null);
  const [validToDate, setValidToDate] = useState<Dayjs | null>(null);

  const role = grantableRoles.find((candidate) => candidate.code === roleCode) ?? null;
  const systemAllowed = role?.resourceScopeTypes.includes(SYSTEM_SCOPE_TYPE) ?? false;

  const resetSelection = (code: string): void => {
    setRoleCode(code);
    const selected = grantableRoles.find((candidate) => candidate.code === code) ?? null;
    if (selected?.organizationAllowed) setScope("ORGANIZATION");
    else if (selected?.administrativeAreaAllowed) setScope("AREA");
    else if (selected?.resourceScopeTypes.includes(SYSTEM_SCOPE_TYPE)) setScope("SYSTEM");
    else setScope(null);
  };

  const canSubmit = role != null
    && (scope === "SYSTEM"
      || (scope === "ORGANIZATION" && organizationId != null)
      || (scope === "AREA" && areaId != null))
    && (immediate || validFromDate != null);

  const handleSubmit = (): void => {
    if (!role || scope == null || !canSubmit) {
      return;
    }
    const validFrom = immediate
      ? new Date(Date.now() + 60_000).toISOString()
      : (validFromDate as Dayjs).startOf("day").toISOString();
    onSubmit({
      roleCode: role.code,
      organizationId: scope === "ORGANIZATION" ? organizationId : null,
      administrativeAreaId: scope === "AREA" ? areaId : null,
      dataScopes: scope === "SYSTEM"
        ? [{ scopeType: SYSTEM_SCOPE_TYPE, scopeId: SYSTEM_SCOPE_ID }]
        : [],
      validFrom,
      validTo: validToDate ? validToDate.endOf("day").toISOString() : null,
    });
  };

  return (
    <Modal
      open={open}
      title={`Cấp vai trò cho ${accountLabel}`}
      okText="Xác nhận cấp vai trò"
      cancelText="Hủy"
      okButtonProps={{ disabled: !canSubmit, loading: submitting }}
      onOk={handleSubmit}
      onCancel={onCancel}
      destroyOnHidden
    >
      <Form layout="vertical" component={false}>
        <Form.Item label="Vai trò được cấp" required>
          <Select
            aria-label="Vai trò được cấp"
            placeholder="Chọn vai trò"
            showSearch
            optionFilterProp="label"
            virtual={false}
            value={roleCode}
            onChange={resetSelection}
            options={grantableRoles.map((option) => ({
              value: option.code,
              label: option.displayName,
            }))}
          />
        </Form.Item>

        {role != null && (
          <Form.Item label="Phạm vi áp dụng" required>
            <Radio.Group
              aria-label="Phạm vi áp dụng"
              value={scope}
              onChange={(event) => setScope(event.target.value as ScopeChoice)}
            >
              {role.organizationAllowed && (
                <Radio.Button value="ORGANIZATION">Đơn vị công tác</Radio.Button>
              )}
              {role.administrativeAreaAllowed && (
                <Radio.Button value="AREA">Địa bàn</Radio.Button>
              )}
              {systemAllowed && <Radio.Button value="SYSTEM">Toàn hệ thống</Radio.Button>}
            </Radio.Group>
          </Form.Item>
        )}

        {scope === "ORGANIZATION" && (
          <Form.Item label="Đơn vị" required>
            <Select
              aria-label="Đơn vị áp dụng"
              placeholder="Chọn đơn vị"
              showSearch
              optionFilterProp="label"
              virtual={false}
              value={organizationId}
              onChange={setOrganizationId}
              options={orgOptions.map((option) => ({ value: option.id, label: option.name }))}
            />
          </Form.Item>
        )}

        {scope === "AREA" && (
          <Form.Item label="Địa bàn" required>
            <Select
              aria-label="Địa bàn áp dụng"
              placeholder="Chọn địa bàn"
              showSearch
              optionFilterProp="label"
              virtual={false}
              value={areaId}
              onChange={setAreaId}
              options={areaOptions.map((option) => ({ value: option.id, label: option.name }))}
            />
          </Form.Item>
        )}

        <Form.Item label="Bắt đầu hiệu lực" required>
          <Radio.Group
            aria-label="Bắt đầu hiệu lực"
            value={immediate}
            onChange={(event) => setImmediate(event.target.value as boolean)}
          >
            <Radio value>Ngay bây giờ</Radio>
            <Radio value={false}>Chọn ngày</Radio>
          </Radio.Group>
        </Form.Item>
        {!immediate && (
          <Form.Item label="Ngày bắt đầu" required>
            <DatePicker
              aria-label="Ngày bắt đầu"
              style={{ width: "100%" }}
              value={validFromDate}
              onChange={setValidFromDate}
            />
          </Form.Item>
        )}
        <Form.Item label="Ngày kết thúc (không bắt buộc)">
          <DatePicker
            aria-label="Ngày kết thúc"
            style={{ width: "100%" }}
            value={validToDate}
            onChange={setValidToDate}
          />
        </Form.Item>

        {error != null && (
          <Alert
            type="error"
            showIcon
            role="alert"
            message={describeAdminError(error.code)}
            description={error.requestId
              ? `Mã hỗ trợ: ${error.requestId}. Kiểm tra lại thông tin hoặc tải dữ liệu mới trước khi gửi lại.`
              : "Kiểm tra lại thông tin hoặc tải dữ liệu mới trước khi gửi lại."}
          />
        )}
      </Form>
    </Modal>
  );
}
